import {
  CommonResponse,
  SCHEDULE_ADD,
  SCHEDULE_ANNIVERSARY,
  SCHEDULE_LIST_GROUPS,
  SCHEDULE_SETTINGS,
  SCHEDULE_SETTING_CREATE,
  netClient
} from '@/apis/api'
import { ref, type Ref } from 'vue'
import type { ComplexDate, LifeDTO, MemorialServiceDTO } from '../life/life'
import type { FileDTO } from '../media/media'
import type { Name } from '../user/users'
import { formatDateToYYYYMMDD } from '@/utils/time'
import { useLocalStorage } from '@vueuse/core'
import { CACHE_KEY_ANNIVERSARY, CACHE_KEY_SCHEDULE } from '@/constants'
import { defineStore } from 'pinia'

const _isCommenDialogOpen = ref<boolean>(false)
export const isCommenDialogOpen = _isCommenDialogOpen

export const scheduleLife: Ref<LifeDTO | undefined> = ref()

export function closeScheduleSetting() {
  isCommenDialogOpen.value = false
}

export let onScheduleSettingDone: () => void

export function registerScheduleDone(listener: () => void) {
  onScheduleSettingDone = listener
}

export interface LifeCommenSettingDTO {
  id: string
  lifeId: string
  userId: string
  key: string
  name: string
  date: string
  calType: number
  enable: boolean
}

export interface CreateCommenSettingCommand {
  lifeId: string
  settings: Array<LifeCommenSettingDTO>
}

export interface GetScheduleSettingResponse {
  settings: Record<string, Array<LifeCommenSettingDTO>>
}

export function getLifeCommenSetting(lifeId: string) {
  return netClient.request<CommonResponse<GetScheduleSettingResponse>>(SCHEDULE_SETTINGS, 'get', { lifeId: lifeId })
}

export function createLifeCommenSettings(settingsCommand: CreateCommenSettingCommand) {
  return netClient.request<CommonResponse<any>>(SCHEDULE_SETTING_CREATE, 'post', settingsCommand)
}

export interface ListAnniversariesResponse {
  page: number
  pageSize: number
  total: number
  totalPages: number
  items: Array<LifeAnniversaryDTO>
}

export interface LifeAnniversaryDTO {
  id: string
  lifeId: string
  life: LifeMinDTO
  userId: string
  settingId: string
  key: string
  anniversaryName: string
  anniversaryDate: number
  no: number
}

export interface LifeMinDTO {
  id: string
  name: Name
  avatar: FileDTO
  birthday: ComplexDate | null
  dayDied: ComplexDate | null
  passAway: boolean
  creator: string
  serviceShare: boolean
}

export function listAnniversaries(
  lifeId: string,
  page: number,
  pageSize: number,
  begin: string,
  end: string,
  sort: string
) {
  return netClient.request<CommonResponse<ListAnniversariesResponse>>(SCHEDULE_ANNIVERSARY, 'get', {
    page: page,
    pageSize: pageSize,
    lifeId: lifeId,
    sort: sort,
    begin: begin,
    end: end
  })
}

const _isScheduleActionDialogOpen = ref<boolean>(false)
export const isScheduleActionDialogOpen = _isScheduleActionDialogOpen

export const lifeAnniversary = useLocalStorage<LifeAnniversaryDTO | undefined>(CACHE_KEY_ANNIVERSARY, undefined)
export const lifeScheduleData = useLocalStorage<LifeDTO | undefined>(CACHE_KEY_SCHEDULE, undefined)

export function closeScheduleAction() {
  isScheduleActionDialogOpen.value = false
}

export interface AddCustomizedSettingCommand {
  lifeId: string
  settingName: string
  settingDate: string
  calType: number
}

export function scheduleAddCustomizedSetting(addCommand: AddCustomizedSettingCommand) {
  return netClient.request<CommonResponse<any>>(SCHEDULE_ADD, 'post', addCommand)
}

export interface AnniversaryGroupDTO {
  key: string
  anniversaryName: string
  anniversaryDate: number
  userId: string
  no: number
  lives: Array<LifeMinDTO>
  firstAnniversaryId: string
  calType?: number
}

export interface ListAnniversaryGroupResponse {
  page: number
  pageSize: number
  total: number
  totalPages: number
  items: Array<AnniversaryGroupDTO>
}

export function listScheduleAnniversaryGroup(
  lifeId: string | null,
  page: number,
  pageSize: number,
  begin: Date,
  end: Date,
  sort: string
) {
  return netClient.request<CommonResponse<ListAnniversaryGroupResponse>>(SCHEDULE_LIST_GROUPS, 'get', {
    lifeId: lifeId,
    page: page,
    pageSize: pageSize,
    sort: sort,
    begin: formatDateToYYYYMMDD(begin),
    end: formatDateToYYYYMMDD(end)
  })
}

export const ACTION_DAYS = 15

interface ActionDialogParams {
  name: string
  tag: string
  key: string
  type: string
  altarId: string
  theme: string
  offeringId: string
}
/**
 * name
 * tag
 * key
 * type
 * altarId
 * theme
 * offeringId
 */
export const useScheduleStore = defineStore('schedule', () => {
  const actionDialogParams = ref<ActionDialogParams>()
  const setActionDialogParams = (params: ActionDialogParams) => {
    actionDialogParams.value = params
  }
  const getActionDialogParams = () => {
    return actionDialogParams.value
  }
  return {
    setActionDialogParams,
    getActionDialogParams
  }
})
